// Admin — Shift Management page.
// Spec: docs/admin-shift-management.md §7.1
// Ca được gom thành thẻ theo phòng ban (thứ tự như TYPE_DEPARTMENT), lọc bằng thanh
// nhóm phía trên. Cột Tên ca (đã bỏ phần giờ) rồi đến cột Ca làm việc (giờ ca).

import { Button, Empty, Skeleton, Switch, Table, Tag, Tooltip, notification } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  MoonOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useMemo, useState } from 'react';
import PageTitle from 'components/page-title';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { TYPE_DEPARTMENT } from 'constants/common';
import { Shift } from 'types/shift';
import CreateShiftModal from './components/create-shift-modal';
import EditShiftModal from './components/edit-shift-modal';
import DeleteShiftModal from './components/delete-shift-modal';
import { mapShiftError } from './helpers/shift-error';
import {
  formatDuration,
  isOvernight,
  nameTimeMismatch,
  nameWithoutTime,
} from './helpers/shift-time';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import styles from './styles.module.scss';

const ALL = 'ALL';
const NO_DEPARTMENT = 'NONE';

// Thứ tự thẻ = thứ tự phòng ban trong TYPE_DEPARTMENT; ca chưa gán phòng ban xếp cuối.
const DEPARTMENT_ORDER: string[] = [...Object.values(TYPE_DEPARTMENT), NO_DEPARTMENT];

// Chấm màu nhận diện nhóm (chỉ trang trí) — lấy từ dải màu Hoàng hôn.
const DEPARTMENT_DOT: Record<string, string> = {
  [TYPE_DEPARTMENT.HR]: '#C2337D',
  [TYPE_DEPARTMENT.TECHNICAL]: '#232C6E',
  [TYPE_DEPARTMENT.MAKEUP]: '#F78FAE',
  [TYPE_DEPARTMENT.MC]: '#FEA88C',
  [TYPE_DEPARTMENT.IDOL]: '#B98AF0',
  [TYPE_DEPARTMENT.CONTENT]: '#FFC078',
  [TYPE_DEPARTMENT.STYLE_LIST]: '#EE86C8',
  [TYPE_DEPARTMENT.DANCE_TEACHER]: '#0F7A46',
  [TYPE_DEPARTMENT.MANAGER]: '#9A1F61',
  [NO_DEPARTMENT]: '#9A8F9E',
};

const groupKey = (s: Shift) => s.department || NO_DEPARTMENT;
const groupLabel = (key: string) =>
  key === NO_DEPARTMENT ? 'Chưa gán phòng ban' : DEPARTMENT_LABEL[key] || key;

const byStartTime = (a: Shift, b: Shift) =>
  a.startTime.localeCompare(b.startTime) ||
  a.endTime.localeCompare(b.endTime) ||
  a.name.localeCompare(b.name);

const ShiftManager = () => {
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [filter, setFilter] = useState<string>(ALL);
  const [showInactive, setShowInactive] = useState(false);
  const [createFor, setCreateFor] = useState<string | null | undefined>(undefined);
  const [editTarget, setEditTarget] = useState<Shift | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Shift | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    const fetchShifts = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.attendanceShifts);
        // Tolerate both `res.data` array and wrapped `res.data.data` shapes.
        const list: Shift[] = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : [];
        if (!cancelled) setShifts(list);
      } catch (err) {
        if (!cancelled) notification.error({ message: mapShiftError(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchShifts();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const inactiveCount = useMemo(() => shifts.filter((s) => !s.isActive).length, [shifts]);
  const visible = useMemo(
    () => (showInactive ? shifts : shifts.filter((s) => s.isActive)),
    [shifts, showInactive]
  );

  // [[phòng ban, các ca đã xếp theo giờ bắt đầu], ...] theo DEPARTMENT_ORDER.
  const groups = useMemo(() => {
    const map = new Map<string, Shift[]>();
    visible.forEach((s) => {
      const key = groupKey(s);
      map.set(key, [...(map.get(key) || []), s]);
    });
    const known = DEPARTMENT_ORDER.filter((k) => map.has(k));
    const unknown = Array.from(map.keys()).filter((k) => !DEPARTMENT_ORDER.includes(k));
    return [...known, ...unknown].map((k) => [k, (map.get(k) || []).sort(byStartTime)] as const);
  }, [visible]);

  // Nhóm đang lọc biến mất (xoá ca cuối cùng / tắt "Hiện ca đã tắt") → về "Tất cả".
  useEffect(() => {
    if (filter !== ALL && !loading && !groups.some(([k]) => k === filter)) setFilter(ALL);
  }, [groups, filter, loading]);

  const shownGroups = filter === ALL ? groups : groups.filter(([k]) => k === filter);

  const filterOptions = [
    {
      value: ALL,
      label: (
        <span className={styles.filterItem}>
          Tất cả <span className={styles.filterCount}>{visible.length}</span>
        </span>
      ),
    },
    ...groups.map(([key, list]) => ({
      value: key,
      label: (
        <span className={styles.filterItem}>
          <i className={styles.dot} style={{ background: DEPARTMENT_DOT[key] }} />
          {groupLabel(key)} <span className={styles.filterCount}>{list.length}</span>
        </span>
      ),
    })),
  ];

  const columns: ColumnsType<Shift> = [
    {
      title: 'Tên ca',
      key: 'name',
      width: 180,
      render: (_: any, s: Shift) => {
        const label = nameWithoutTime(s.name);
        const staleTime = nameTimeMismatch(s.name, s.startTime, s.endTime);
        return (
          <span className={styles.nameCell} title={s.name}>
            <span className={label ? styles.name : styles.muted}>{label || '—'}</span>
            {staleTime && (
              <Tooltip
                title={`Tên ca ghi ${staleTime} nhưng giờ thật là ${s.startTime}-${s.endTime} (giờ đã được sửa sau khi tạo ca).`}
              >
                <WarningOutlined className={styles.staleIcon} aria-label="Tên ca ghi giờ cũ" />
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: 'Ca làm việc',
      key: 'time',
      render: (_: any, s: Shift) => {
        const overnight = isOvernight(s.startTime, s.endTime);
        return (
          <div className={styles.timeCell}>
            <span className={styles.timeRange}>
              {s.startTime}
              <span className={styles.timeArrow}>→</span>
              {s.endTime}
              {overnight && <sup className={styles.nextDay}>+1</sup>}
            </span>
            <span className={styles.timeMeta}>
              {formatDuration(s.startTime, s.endTime)}
              {overnight && (
                <span className={styles.overnight}>
                  <MoonOutlined /> Qua đêm
                </span>
              )}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'default'} className={styles.statusTag}>
          {v ? 'Đang dùng' : 'Tắt'}
        </Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      align: 'right' as const,
      width: 96,
      render: (_: any, record: Shift) => (
        <div className={styles.actions}>
          <Tooltip title="Sửa giờ ca">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Sửa giờ ca"
              onClick={() => setEditTarget(record)}
            />
          </Tooltip>
          <Tooltip title="Xoá ca">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label="Xoá ca"
              onClick={() => setDeleteTarget(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageTitle
        title="Ca làm"
        extra={
          <CustomButton
            bgColor="primary-green"
            text="Tạo ca làm"
            icon={<PlusOutlined />}
            onClick={() => setCreateFor(null)}
          />
        }
      />

      <div className={styles.toolbar}>
        <div className={styles.chips} role="tablist" aria-label="Lọc theo phòng ban">
          {filterOptions.map((o) => (
            <button
              key={o.value}
              type="button"
              role="tab"
              aria-selected={filter === o.value}
              className={filter === o.value ? `${styles.chip} ${styles.chipActive}` : styles.chip}
              onClick={() => setFilter(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
        {inactiveCount > 0 && (
          <label className={styles.inactiveToggle}>
            <Switch size="small" checked={showInactive} onChange={setShowInactive} />
            Hiện ca đã tắt ({inactiveCount})
          </label>
        )}
      </div>

      {loading && !shifts.length ? (
        <div className={styles.card}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      ) : !shownGroups.length ? (
        <div className={styles.card}>
          <Empty description="Chưa có ca làm nào" />
        </div>
      ) : (
        <div className={styles.groups}>
          {shownGroups.map(([key, list]) => (
            <section key={key} className={styles.card}>
              <header className={styles.cardHeader}>
                <div className={styles.cardTitle}>
                  <i className={styles.dot} style={{ background: DEPARTMENT_DOT[key] }} />
                  <h2>{groupLabel(key)}</h2>
                  <span className={styles.cardCount}>{list.length} ca</span>
                </div>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateFor(key === NO_DEPARTMENT ? null : key)}
                >
                  Thêm ca
                </Button>
              </header>
              <Table
                className={styles.table}
                columns={columns}
                rowKey="id"
                size="middle"
                pagination={false}
                dataSource={list}
                loading={loading}
                rowClassName={(r) => (r.isActive ? '' : styles.inactiveRow)}
                scroll={{ x: 560 }}
              />
            </section>
          ))}
        </div>
      )}

      <CreateShiftModal
        open={createFor !== undefined}
        defaultDepartment={createFor}
        onClose={() => setCreateFor(undefined)}
        onSuccess={triggerRefresh}
      />
      <EditShiftModal
        open={!!editTarget}
        shift={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={triggerRefresh}
      />
      <DeleteShiftModal
        open={!!deleteTarget}
        shift={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={triggerRefresh}
      />
    </div>
  );
};

export default ShiftManager;
